import {
  autoUpdate,
  flip,
  offset,
  safePolygon,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  useDismiss,
  FloatingPortal,
  useId,
  arrow,
  shift,
  FloatingArrow
} from '@floating-ui/react';
import React, { memo, PropsWithChildren, useRef, useState } from 'react';

import { Props } from './types';
const ARROW_HEIGHT = 7;

/**
 * @deprecated Do not use this component. Use the base components at the root of the UI folder instead.
 */
const Tooltip = ({
  children,
  tooltipContent,
  placement = 'top',
  offset: offsetValue = 2,
  disabled = false,
  interactive = false,
  arrow: arrowEnabled = true,
  arrowHeight = ARROW_HEIGHT,
  className = ''
}: PropsWithChildren<Props>) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(ARROW_HEIGHT + offsetValue),
      flip({
        fallbackAxisSideDirection: 'start'
      }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate
  });

  const hover = useHover(context, {
    move: false,
    enabled: !disabled,
    handleClose: interactive ? safePolygon() : null
  });

  const role = useRole(context, { role: 'tooltip' });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, role, dismiss]);

  const id = useId();

  if (disabled) {
    return <>{children}</>;
  }

  const referenceProps = getReferenceProps({
    'aria-describedby': isOpen ? id : undefined
  });

  return (
    <>
      <span ref={refs.setReference} {...referenceProps}>
        {children}
      </span>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            id={id}
            className={`z-50 max-w-sm px-3 py-2 text-sm font-medium text-uniform-white-base bg-gray-900 rounded-lg shadow-sm dark:bg-gray-700 tooltip-content ${className}`}>
            {tooltipContent}
            {arrowEnabled && (
              <FloatingArrow ref={arrowRef} context={context} height={arrowHeight} />
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default memo(Tooltip);
